/**
 * AI 跟单兑换（convert v2.5）：法币 ↔ USDT（type 2/3）、USDC → USDT（type 7）。
 * 试算/下单 payload 复用 Convert 页 convertV2；下单走 POST /convert/order。
 */
import { buildPreviewPayload, buildUniversalOrderPayload, findConfigForPair } from "@/components/Pages/Convert/convertV2";
import { post_convertOrder } from "@/api/v4/convert";
import { AICOPY_USDC_CODE, AICOPY_USDC_CURRENCY_ID } from "./aicopyCurrencies";

export const AICOPY_CONVERT_STABLE = "USDT";

export function getEnabledConvertConfigs(configList) {
  return (configList || []).filter((c) => c.enabled !== 0);
}

function buildCurrencyOptionMap(configs) {
  const map = new Map();
  configs.forEach((c) => {
    if (c.currencyCode) {
      const upper = String(c.currencyCode).toUpperCase();
      map.set(upper, { code: upper, currencyId: c.currencyId });
    }
    if (c.cryptoCurrencyCode) {
      const upper = String(c.cryptoCurrencyCode).toUpperCase();
      map.set(upper, {
        code: upper,
        currencyId: c.cryptoCurrencyId,
        cryptoCurrencyId: c.cryptoCurrencyId,
      });
    }
  });
  return map;
}

function withEnabledConvertContext(configs) {
  const enabled = getEnabledConvertConfigs(configs);
  return { enabled, map: buildCurrencyOptionMap(enabled) };
}

function isUniversalOrderPayloadComplete(payload) {
  return !!(payload?.fromCurrencyId && payload?.toCurrencyId && payload?.sourceAmount != null);
}

function buildAicopyOrderPayload(fromCode, toCode, sourceAmount, configs) {
  const { enabled, map } = withEnabledConvertContext(configs);
  return buildUniversalOrderPayload(fromCode, toCode, sourceAmount, map, enabled);
}

function resolveAicopyUsdtCurrencyId(configs) {
  for (const c of getEnabledConvertConfigs(configs)) {
    const from = String(c.currencyCode || "").toUpperCase();
    const to = String(c.cryptoCurrencyCode || "").toUpperCase();
    if (to === AICOPY_CONVERT_STABLE && c.cryptoCurrencyId != null) return c.cryptoCurrencyId;
    if (from === AICOPY_CONVERT_STABLE && c.currencyId != null) return c.currencyId;
  }
  return null;
}

function buildAicopyUsdcToUsdtOrderPayload(sourceAmount, configs) {
  const { enabled, map } = withEnabledConvertContext(configs);
  const payload = buildUniversalOrderPayload(AICOPY_USDC_CODE, AICOPY_CONVERT_STABLE, sourceAmount, map, enabled);
  return {
    fromCurrencyId: payload.fromCurrencyId ?? AICOPY_USDC_CURRENCY_ID,
    toCurrencyId: payload.toCurrencyId ?? resolveAicopyUsdtCurrencyId(configs),
    sourceAmount: payload.sourceAmount,
  };
}

function buildAicopyUsdcToUsdtPreviewPayload(sourceAmount, configs) {
  const { enabled, map } = withEnabledConvertContext(configs);
  const preview = buildPreviewPayload(AICOPY_USDC_CODE, AICOPY_CONVERT_STABLE, sourceAmount, map, enabled);
  return {
    ...preview,
    currencyId: preview.currencyId ?? AICOPY_USDC_CURRENCY_ID,
    cryptoCurrencyId: preview.cryptoCurrencyId ?? resolveAicopyUsdtCurrencyId(configs),
    type: preview.type ?? 7,
  };
}

/** 按 v2 语义匹配：currencyCode=from，cryptoCurrencyCode=to，type 固定 */
function findAicopyConvertConfig(configs, fromCode, toCode, convertType) {
  const from = String(fromCode || "").toUpperCase();
  const to = String(toCode || "").toUpperCase();
  if (!from || !to) return null;
  return (
    getEnabledConvertConfigs(configs).find(
      (c) =>
        Number(c.type) === convertType &&
        String(c.currencyCode || "").toUpperCase() === from &&
        String(c.cryptoCurrencyCode || "").toUpperCase() === to
    ) || null
  );
}

/** 法币 → USDT（convertType 2） */
export function findAicopyFiatToUsdtConfig(configs, fiatCode) {
  const fiat = String(fiatCode || "").toUpperCase();
  if (!fiat || fiat === AICOPY_CONVERT_STABLE || fiat === AICOPY_USDC_CODE) return null;
  return findAicopyConvertConfig(configs, fiat, AICOPY_CONVERT_STABLE, 2);
}

/** USDC → USDT（convertType 7 稳定买稳定） */
export function findAicopyUsdcToUsdtConfig(configs) {
  return (
    findAicopyConvertConfig(configs, AICOPY_USDC_CODE, AICOPY_CONVERT_STABLE, 7) ||
    findConfigForPair(getEnabledConvertConfigs(configs), AICOPY_USDC_CODE, AICOPY_CONVERT_STABLE)
  );
}

/** 支付币种 → USDT 的兑换配置（法币或 USDC） */
export function findAicopyPayToUsdtConfig(configs, payCode) {
  const code = String(payCode || "").toUpperCase();
  if (code === AICOPY_USDC_CODE) return findAicopyUsdcToUsdtConfig(configs);
  return findAicopyFiatToUsdtConfig(configs, payCode);
}

/** USDT → 法币（convertType 3） */
export function findAicopyUsdtToFiatConfig(configs, fiatCode) {
  const fiat = String(fiatCode || "").toUpperCase();
  if (!fiat || fiat === AICOPY_CONVERT_STABLE) return null;
  return findAicopyConvertConfig(configs, AICOPY_CONVERT_STABLE, fiat, 3);
}

/** 结算/estimate 接口用的目标法币 currencyId（type 3：from=USDT，to=法币） */
export function getAicopyUsdtToFiatTargetId(config) {
  if (!config) return null;
  const from = String(config.currencyCode || "").toUpperCase();
  const to = String(config.cryptoCurrencyCode || "").toUpperCase();
  if (from === AICOPY_CONVERT_STABLE && to && to !== AICOPY_CONVERT_STABLE) {
    return config.cryptoCurrencyId ?? null;
  }
  return null;
}

export function buildAicopyFiatToUsdtPreview(fiatCode, sourceAmount, configs) {
  const { enabled, map } = withEnabledConvertContext(configs);
  return buildPreviewPayload(fiatCode, AICOPY_CONVERT_STABLE, sourceAmount, map, enabled);
}

export function buildAicopyUsdcToUsdtPreview(sourceAmount, configs) {
  return buildAicopyUsdcToUsdtPreviewPayload(sourceAmount, configs);
}

export function buildAicopyPayToUsdtOrder(payCode, sourceAmount, configs) {
  const code = String(payCode || "").toUpperCase();
  if (code === AICOPY_USDC_CODE) {
    return buildAicopyUsdcToUsdtOrderPayload(sourceAmount, configs);
  }
  return buildAicopyOrderPayload(payCode, AICOPY_CONVERT_STABLE, sourceAmount, configs);
}

export function buildAicopyUsdtToFiatPreview(fiatCode, sourceAmount, configs) {
  const { enabled, map } = withEnabledConvertContext(configs);
  return buildPreviewPayload(AICOPY_CONVERT_STABLE, fiatCode, sourceAmount, map, enabled);
}

export function buildAicopyUsdtToFiatOrder(fiatCode, sourceAmount, configs) {
  return buildAicopyOrderPayload(AICOPY_CONVERT_STABLE, fiatCode, sourceAmount, configs);
}

export function validateAicopyFiatToUsdtConvert(configs, fiatCode, sourceAmount) {
  const config = findAicopyFiatToUsdtConfig(configs, fiatCode);
  if (!config?.currencyId || !config?.cryptoCurrencyId) {
    return { ok: false, reason: "config" };
  }
  const previewPayload = buildAicopyFiatToUsdtPreview(fiatCode, sourceAmount, configs);
  if (!previewPayload?.currencyId || !previewPayload?.cryptoCurrencyId || previewPayload?.type == null) {
    return { ok: false, reason: "payload" };
  }
  const orderPayload = buildAicopyOrderPayload(fiatCode, AICOPY_CONVERT_STABLE, sourceAmount, configs);
  if (!isUniversalOrderPayloadComplete(orderPayload)) {
    return { ok: false, reason: "payload" };
  }
  return { ok: true, config, previewPayload, orderPayload };
}

export function validateAicopyUsdcToUsdtConvert(configs, sourceAmount) {
  const config = findAicopyUsdcToUsdtConfig(configs);
  if (!config) {
    return { ok: false, reason: "config" };
  }
  const previewPayload = buildAicopyUsdcToUsdtPreviewPayload(sourceAmount, configs);
  if (!previewPayload?.currencyId || !previewPayload?.cryptoCurrencyId || previewPayload?.type == null) {
    return { ok: false, reason: "payload" };
  }
  const orderPayload = buildAicopyUsdcToUsdtOrderPayload(sourceAmount, configs);
  if (!isUniversalOrderPayloadComplete(orderPayload)) {
    return { ok: false, reason: "payload" };
  }
  return { ok: true, config, previewPayload, orderPayload };
}

/** 法币 / USDC 支付前：试算与下单参数校验 */
export function validateAicopyPayToUsdtConvert(configs, payCode, sourceAmount) {
  const code = String(payCode || "").toUpperCase();
  if (code === AICOPY_USDC_CODE) return validateAicopyUsdcToUsdtConvert(configs, sourceAmount);
  return validateAicopyFiatToUsdtConvert(configs, payCode, sourceAmount);
}

export function executeAicopyConvertToUsdtOrder(orderPayload, config) {
  return post_convertOrder(orderPayload, config);
}
